import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Popover, ArrowContainer, PopoverAlign } from 'react-tiny-popover';
import { useTranslation, Trans } from 'react-i18next';
import { COLORS } from '../../styles/variables';
import { Container, Title, Copy, IconWrap } from './InfoPopover.styles';
import InfoIcon from '../../assets/icons/InfoIcon';

interface InfoPopoverProps {
  title?: string;
  description: string;
  iconSize?: number;
  align?: PopoverAlign;
  containerPosition?: {
    [key: string]: string;
  };
  iconColor?: string;
  bgColor?: string;
  triggerTestId?: string;
  iconElevated?: boolean;
  isVehicleCardComponent?: boolean;
}

const InfoPopover: React.FC<InfoPopoverProps> = ({
  title,
  description,
  bgColor,
  align,
  containerPosition,
  iconColor,
  triggerTestId,
  iconElevated,
}) => {
  const { t } = useTranslation();

  const [showInfoPopover, setShowInfoPopover] = useState(false);
  const elementRef = useRef<HTMLDivElement>(null);
  const defaultContainerPosition = { zIndex: '2000' };
  const closePopover = useCallback(() => {
    setShowInfoPopover(false);
  }, []);

  const togglePopover = useCallback((e: any) => {
    e.stopPropagation();
    setShowInfoPopover((value) => !value);
  }, []);

  useEffect(() => {
    if (!showInfoPopover)
      return () => {
        // noop
      };

    document.addEventListener('click', closePopover);

    return () => {
      document.removeEventListener('click', closePopover);
    };
  }, [closePopover, showInfoPopover]);

  return (
    <Popover
      isOpen={showInfoPopover}
      positions={['bottom']}
      align={align || 'center'}
      padding={0}
      onClickOutside={closePopover}
      containerStyle={containerPosition || defaultContainerPosition}
      parentElement={elementRef.current || undefined}
      content={({ position, childRect, popoverRect }) => (
        <ArrowContainer
          position={position}
          childRect={childRect}
          popoverRect={popoverRect}
          arrowColor={bgColor || COLORS.black}
          arrowSize={10}
          arrowStyle={{ opacity: 1 }}
          className="popover-arrow-container"
          arrowClassName="popover-arrow"
        >
          <Container data-testid="info-popover-container" bgColor={bgColor}>
            {title && <Title>{title}</Title>}
            <Copy>
              <Trans
                i18nKey={description}
                // the global reset css imported in several feature apps
                // contains globally applied oppinionated styling
                components={{ italic: <span style={{ fontStyle: 'italic' }} /> }}
              />
            </Copy>
          </Container>
        </ArrowContainer>
      )}
    >
      <IconWrap
        onClick={togglePopover}
        aria-label={t('vlp.vehicleResults.popoverInformation')}
        data-testid={triggerTestId}
        iconElevated={iconElevated}
      >
        <div ref={elementRef}>
          <InfoIcon color={iconColor} />
        </div>
      </IconWrap>
    </Popover>
  );
};

export default InfoPopover;
